import { DateStrategy } from "app/shared/model/EditProductOffering";

export type ProductTypeName =
    | "Course"
    | "Accommodation"
    | "AddOn"
    | "Transfer"
    | "Package";

const STRATEGY = {
    Recurrence: DateStrategy.Recurrence as unknown as number, // 1
    FixedDuration: DateStrategy["Fixed Duration"] as unknown as number, // 2
    OfferingDates: DateStrategy["Offering Dates"] as unknown as number, // 3
    SpecificStartDates: DateStrategy["Specific Start Dates"] as unknown as number, // 4
};

export type ProductMeta = {
    unitTypeText?: string;
    unitTypeId?: number;
    dateStrategyAssignments?: Array<{ strategyTypeId?: number | null }>;
    [key: string]: unknown;
};

const norm = (s?: string) => String(s ?? "").trim().toLowerCase();

const FIXED_PRICE_UNIT_TYPE_ID = 2;

const isFixedPriceByUnitType = (meta?: ProductMeta): boolean => {
    const id = meta?.unitTypeId;
    if (typeof id === "number" && Number.isFinite(id) && id === FIXED_PRICE_UNIT_TYPE_ID) return true;

    const text = norm(meta?.unitTypeText);
    return text.includes("fixed") && text.includes("price");
};

const hasOfferingDatesAssignment = (meta?: ProductMeta): boolean => {
    const list = meta?.dateStrategyAssignments;
    if (!Array.isArray(list) || list.length === 0) return false;

    return list.some(
        (x) =>
            !(x as { isArchived?: boolean }).isArchived &&
            Number(x?.strategyTypeId) === STRATEGY.OfferingDates
    );
};

const isFixedPriceAddOn = (meta?: ProductMeta): boolean => {
    if (isFixedPriceByUnitType(meta)) return true;
    if (hasOfferingDatesAssignment(meta)) return true;

    return false;
};

export const getAllowedDateStrategyTypeIds = (
    productTypeName?: string,
    productMeta?: ProductMeta
): number[] => {
    const type = String(productTypeName ?? "").trim() as ProductTypeName;

    switch (type) {
        case "Course":
            return [STRATEGY.Recurrence, STRATEGY.SpecificStartDates, STRATEGY.FixedDuration];

        case "Accommodation":
            return [STRATEGY.Recurrence];

        case "Transfer":
            return [STRATEGY.OfferingDates];

        case "Package":
            return [STRATEGY.Recurrence, STRATEGY.SpecificStartDates, STRATEGY.FixedDuration];

        case "AddOn":
            return isFixedPriceAddOn(productMeta)
                ? [STRATEGY.OfferingDates]
                : [STRATEGY.Recurrence];

        default:
            return [
                STRATEGY.Recurrence,
                STRATEGY.SpecificStartDates,
                STRATEGY.FixedDuration,
                STRATEGY.OfferingDates,
            ];
    }
};

export const toStrategyOptions = (ids: number[]) =>
    ids.map((id) => {
        const label = getDateStrategyTypeName(id);
        return { value: label, label };
    });

export const getDateStrategyTypeName = (strategyTypeId: number): string => {
    const id = Number(strategyTypeId);
    if (!Number.isFinite(id) || id <= 0) return "";

    const name = DateStrategy[id as any] as unknown;
    return typeof name === "string" ? name : "";
};

export const toStrategyTypeSelectOptions = (ids: number[]) =>
    ids.map((id) => ({
        value: id,
        label: getDateStrategyTypeName(id) || `Strategy ${id}`,
    }));

export const resolveStrategyTypeId = (
    current: unknown,
    allowedIds: number[],
    productMeta?: ProductMeta
): number | undefined => {
    if (!allowedIds.length) return undefined;

    const currentId = Number(current);
    if (Number.isFinite(currentId) && allowedIds.includes(currentId)) {
        return currentId;
    }

    const assignments = productMeta?.dateStrategyAssignments;
    if (Array.isArray(assignments)) {
        for (const assignment of assignments) {
            const id = Number(assignment?.strategyTypeId);
            if (allowedIds.includes(id)) return id;
        }
    }

    return allowedIds[0];
};